import React from 'react';

import { translate } from '~l10n/client';

import { UIButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { xAgoFormatter } from '~shared/components/helpers';
import { IconDocument, IconFolder } from '~shared/components/icons/standard';
import { Inline } from '~shared/components/Inline';
import { Search } from '~shared/components/Table/update/components/Search';
import { toast } from '~shared/components/Toast';
import {
  DialogQuizItemList,
  commonDateSorter,
} from '~teacher/components/workspaces/DialogQuizItemList/DialogQuizItemList';
import { navigateByFolderId } from '~teacher/components/workspaces/WorkspaceQuizzes/utils/navigateByItemType';
import { useMoveWorkspaceFolders } from '~teacher/hook/workspaces/useMoveWorkspaceFolders';
import { useMoveWorkspaceQuizzes } from '~teacher/hook/workspaces/useMoveWorkspaceQuizzes';
import { useWorkspaceFolders } from '~teacher/hook/workspaces/useWorkspaceFolders';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

function getQuizTitle(itemsToMove: WorkspaceItem[]): string {
  if (!itemsToMove?.length) return '';
  if (itemsToMove?.length === 1) {
    const [itemToMove] = itemsToMove;
    return translate('Move {0}').format(itemToMove.name);
  }

  return translate('Move {0} items').format(itemsToMove.length);
}

function getSuccessToastMessage(itemsToMove: WorkspaceItem[]) {
  if (!itemsToMove.length) return '';
  if (itemsToMove?.length === 1) {
    const [itemToMove] = itemsToMove;
    return translate('{0} moved successfully').format(itemToMove.name);
  }

  return translate('{0} items moved successfully').format(itemsToMove.length);
}

export function MoveWorkspaceItemsDialog({
  onClose,
  isOpen,
  workspaceId,
  workspaceItems,
  workspaceName = '',
  parentId,
  router,
}: {
  onClose: () => void;
  isOpen: boolean;
  workspaceId: string;
  workspaceItems: WorkspaceItem[];
  workspaceName: string;
  parentId: string;
  router;
}) {
  const { data, isLoading } = useWorkspaceFolders({ workspaceId: workspaceId });
  const [selectedFolderId, setSelectedFolderId] = React.useState(parentId);
  const [isMoveDisabled, setIsMoveDisabled] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const workspaceFolderMap = React.useMemo(() => {
    return data?.reduce((map, item) => {
      map[item.id] = item;
      return map;
    }, {});
  }, [data]);

  React.useEffect(() => {
    if (isOpen === false) {
      setSelectedFolderId(null);
    } else {
      if (parentId && workspaceFolderMap?.[parentId]) {
        setSelectedFolderId(parentId);
      }
    }
  }, [isOpen, parentId, workspaceFolderMap]);

  const handleSelection = React.useCallback(
    (item) => {
      if (item.type === 'folder') {
        setSearchQuery('');
        setSelectedFolderId(item.id);
      }
      if (item.id.parentId === selectedFolderId) {
        return;
      }
      setSelectedFolderId(item.id);
      return;
    },
    [selectedFolderId]
  );

  const moveWorkspaceQuizzesMutation = useMoveWorkspaceQuizzes();
  const moveWorkspaceFoldersMutation = useMoveWorkspaceFolders();
  const handleMoveWorkspaceItems = React.useCallback(async () => {
    if (!workspaceItems) return;
    setIsMoveDisabled(true);
    const workspaceItemsMap = workspaceItems.reduce(
      (map, workspaceItem) => {
        if (workspaceItem['type'] === 'quiz') {
          map['workspaceQuizIds'].push(workspaceItem.id);
          map['workspaceQuizzes'].push(workspaceItem);
        } else if (workspaceItem['type'] === 'folder') {
          map['workspaceFolderIds'].push(workspaceItem.id);
          map['workspaceFolders'].push(workspaceItem);
        }

        return map;
      },
      {
        workspaceQuizIds: [],
        workspaceQuizzes: [],
        workspaceFolderIds: [],
        workspaceFolders: [],
      }
    );

    const {
      workspaceQuizIds,
      workspaceQuizzes,
      workspaceFolderIds,
      workspaceFolders,
    } = workspaceItemsMap;

    try {
      await Promise.all([
        moveWorkspaceQuizzesMutation.mutateAsync(
          {
            workspaceId: workspaceId,
            parentFolderId: selectedFolderId,
            workspaceQuizIds: workspaceQuizIds,
          },
          {
            onSuccess: () => {
              if (workspaceQuizzes.length > 0) {
                toast(getSuccessToastMessage(workspaceQuizzes), {
                  tone: 'positive',
                });
              }
            },
          }
        ),
        moveWorkspaceFoldersMutation.mutateAsync(
          {
            workspaceId: workspaceId,
            parentFolderId: selectedFolderId,
            workspaceFolderIds: workspaceFolderIds,
          },
          {
            onSuccess: () => {
              if (workspaceFolders.length > 0) {
                toast(getSuccessToastMessage(workspaceFolders), {
                  tone: 'positive',
                });
              }
            },
          }
        ),
      ]);
      navigateByFolderId({
        workspaceFolderId: selectedFolderId ?? null,
        router: router,
        isDeleted: false,
        workspaceId: workspaceId,
      });
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        toast(`Error: ${error.message}`, { tone: 'critical' });
        console.error(error);
      }
    } finally {
      setIsMoveDisabled(false);
    }
  }, [
    moveWorkspaceFoldersMutation,
    workspaceItems,
    workspaceId,
    moveWorkspaceQuizzesMutation,
    selectedFolderId,
    onClose,
    router,
  ]);

  // columns
  const columns = React.useMemo(
    () => [
      {
        id: 'type',
        disableSortBy: true,
        accessor: (item) => item.type,
        customWidth: '60px',
        Cell: ({ value }) =>
          value === 'quiz' ? <IconDocument /> : <IconFolder />,
      },
      {
        Header: translate('Name'),
        id: 'name',
        accessor: (item) => item.name,
        sortBy: 'alphanumeric',
      },
      {
        Header: translate('Modified'),
        id: 'modified',
        sortType: commonDateSorter,
        customWidth: '160px',
        accessor: (item) => {
          return xAgoFormatter({ date: item.createdDate });
        },
      },
    ],
    []
  );

  const handleBackClick = React.useCallback(() => {
    if (selectedFolderId === null) return;
    const newId = data.find((item) => item.id === selectedFolderId).parentId;
    setSearchQuery('');
    setSelectedFolderId(newId ? String(newId) : null);
  }, [data, selectedFolderId]);
  const parentIdFilter = React.useCallback(
    (data) => {
      if (searchQuery) return data;
      return data.filter((item) => {
        if (workspaceId === 'personal') {
          return item.parentId === selectedFolderId;
        }
        if (item.type === 'quiz') {
          return item.parentFolderId === selectedFolderId;
        }
        return item.parentId === selectedFolderId;
      });
    },
    [selectedFolderId, searchQuery, workspaceId]
  );

  if (!isOpen) {
    return <></>;
  }

  return (
    <Dialog
      onClose={() => {
        onClose();
        setSearchQuery('');
      }}
      id="move-workspace-items-dialog"
      open={isOpen}
      title={getQuizTitle(workspaceItems)}
      closeLabel={translate('Close')}
    >
      <Container className={'pt-2'}>
        <Divider />
      </Container>
      <Container className={'pt-6'}>
        <Search
          name={workspaceName}
          setQuery={setSearchQuery}
          variant={'modal'}
        />
        <div style={{ height: '400px' }}>
          <DialogQuizItemList
            data={data}
            rootIsFinal
            workspaceName={workspaceName}
            onBack={handleBackClick}
            parentId={selectedFolderId}
            onSelection={handleSelection}
            filters={[parentIdFilter]}
            searchQuery={{ id: 'name', value: searchQuery }}
            isLoading={isLoading}
            columns={columns}
            // will make the current parent folder and all its children invisible
            disableRow={(row) =>
              workspaceItems.some((item) => item.id === row.id)
            }
          />
        </div>
        <Container
          className={'justify-end'}
          row
          margin={'20px 0 0 '}
          verticalCenter
        >
          <Inline align="right">
            <UIButton
              onClick={() => {
                onClose();
                setSearchQuery('');
              }}
            >
              {translate('Cancel')}
            </UIButton>
            <UIButton
              onClick={handleMoveWorkspaceItems}
              isDisabled={isMoveDisabled || selectedFolderId === parentId}
              appearance="primary"
              a11yLabel="move-folder-button"
            >
              {translate('Move')}
            </UIButton>
          </Inline>
        </Container>
      </Container>
    </Dialog>
  );
}
